import * as React from "react";
import {
  List,
  Datagrid,
  Show,
  TabbedShowLayout,
  Edit,
  SimpleForm,
  FunctionField,
  TextInput,
  SelectInput,
  BooleanInput,
  required,
  email,
  useRecordContext,
  useNotify,
  ReferenceManyField,
  EditButton,
  TabbedShowLayoutTabs,
} from "react-admin";
import { FollowUpProgramGrid } from "../follow-up-programs";
import { TextField as MuiTextField, Button, Box, Stack, Card } from "@mui/material";
import { RemoteList } from "../../components/RemoteList";
import { ReferralPanel } from "../../components/ReferralPanel";
import { DocumentDownloadButton } from "../../components/DocumentDownloadButton";
import { InfoGrid } from "../../components/InfoGrid";
import { Typography as MuiTypography } from "@mui/material";
import { ACCOUNT_STATUS, GENDER, FRIENDSHIP_STATUS, choicesFromEnum, labelOf } from "../../enums";
import { PersonChip, Section, CardGrid, TechInfo, StatusChip } from "../../components/ui";
import { ProfileHeader, RecordLink, bookingColumns, forfaitColumns, Stars } from "../../components/profile";
import { fmtDate, fmtDateTime, fullName } from "../../utils/format";
import { EnumChip } from "../../components/EnumChip";
import { httpClient } from "../../api/httpClient";
import { API_BASE_URL } from "../../api/config";
import { FrDateField } from "../../components/FrDateField";

const pick = (row, ...keys) => {
  for (const k of keys) {
    const parts = k.split(".");
    let v = row;
    for (const p of parts) v = v == null ? undefined : v[p];
    if (v != null && v !== "") return v;
  }
  return undefined;
};

// Onglet Notes : POST /patients/{id}/notes (body = texte brut).
const NotesPanel = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const [value, setValue] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  if (!record) return null;
  const save = async () => {
    setSaving(true);
    try {
      await httpClient(`${API_BASE_URL}/patients/${record.id}/notes`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(value),
      });
      notify("Notes enregistrées", { type: "success" });
    } catch (e) {
      notify(`Erreur : ${e.message}`, { type: "error" });
    } finally {
      setSaving(false);
    }
  };
  return (
    <Box sx={{ p: 1, maxWidth: 600 }}>
      <Stack spacing={2}>
        <MuiTextField
          label="Notes sur le patient"
          multiline
          minRows={4}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          fullWidth
        />
        <Button variant="contained" onClick={save} disabled={saving || !value.trim()}>
          Enregistrer
        </Button>
      </Stack>
    </Box>
  );
};

// GET /users?type=PATIENT : seul hideTestUsers est pris en compte pour les
// patients (recherche texte / ville ignorées par le backend → retirées ;
// pour chercher un patient : menu « Recherche patient »).
const filters = [
  <BooleanInput
    key="hideTestUsers"
    source="hideTestUsers"
    label="Masquer les comptes de test"
    defaultValue={true}
  />,
  <BooleanInput key="showDeleted" source="showDeleted" label="Afficher les comptes supprimés" />,
];

export const PatientList = () => (
  <List
    filters={filters}
    filterDefaultValues={{ hideTestUsers: true }}
    perPage={25}
    sort={{ field: "creationDate", order: "DESC" }}
  >
    <Datagrid rowClick="show" bulkActionButtons={false}>
      <FunctionField
        label="Patient"
        sortBy="lastName"
        render={(r) => <PersonChip firstName={r.firstName} lastName={r.lastName} email={r.email} photo={r?.photo?.url} resource="patients" />}
      />
      <FunctionField label="Téléphone" render={(r) => r?.phoneNumber || "—"} />
      <FunctionField label="Ville" render={(r) => r?.address?.city || "—"} />
      <EnumChip source="accountStatus" enumObj={ACCOUNT_STATUS} label="Statut" />
      <FrDateField source="creationDate" label="Inscrit le" />
    </Datagrid>
  </List>
);

// Onglet Stats santé : gamification (ArcA/Talismans) + stats santé par mois
// sur les 12 derniers mois. Endpoints :
//   GET /patients/{id}/gamification         → GamificationBalanceResponse
//   GET /patients/{id}/stats/health?from&to → HealthStatsResponseDTO
const PatientStatsPanel = () => {
  const r = useRecordContext();
  const [gam, setGam] = React.useState({ loading: true, data: null, error: null });
  const [hs, setHs] = React.useState({ loading: true, data: null, error: null });
  React.useEffect(() => {
    if (!r?.id) return;
    httpClient(`${API_BASE_URL}/patients/${r.id}/gamification`)
      .then(({ json }) => setGam({ loading: false, data: json, error: null }))
      .catch((e) => setGam({ loading: false, data: null, error: e }));
    // Le backend attend des mois « YYYY-MM » (YearMonth.parse).
    const to = new Date().toISOString().slice(0, 7);
    const fromD = new Date(); fromD.setDate(1); fromD.setMonth(fromD.getMonth() - 11);
    const from = fromD.toISOString().slice(0, 7);
    httpClient(`${API_BASE_URL}/patients/${r.id}/stats/health?from=${from}&to=${to}`)
      .then(({ json }) => setHs({ loading: false, data: json, error: null }))
      .catch((e) => setHs({ loading: false, data: null, error: e }));
  }, [r?.id]);
  if (!r) return null;
  const g = gam.data;
  const months = Array.isArray(hs.data?.byMonth) ? hs.data.byMonth : [];
  return (
    <MuiTypography component="div" sx={{ p: 1 }}>
      <MuiTypography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Gamification & récompenses</MuiTypography>
      {gam.loading ? <MuiTypography variant="body2" color="text.secondary">Chargement…</MuiTypography> : (
        <InfoGrid
          fields={[
            { label: "ArcA / Ura (points)", source: () => g?.ura ?? 0 },
            { label: "Équivalent €", source: () => g?.uraInEuros != null ? `${Number(g.uraInEuros).toFixed(2)} €` : "—" },
            { label: "Ura activé", source: () => g?.uraActivated, type: "bool" },
            { label: "Séances avant activation", source: () => g?.sessionsToActivation ?? 0 },
            { label: "Séances réalisées", source: () => g?.sessionsCompleted ?? 0 },
            { label: "Feedbacks donnés", source: () => g?.feedbacksSubmitted ?? 0 },
            { label: "Nb talismans", source: () => Array.isArray(g?.talismans) ? g.talismans.length : 0 },
            { label: "Jours d'activité quiz", source: () => Array.isArray(g?.quizActivityDates) ? g.quizActivityDates.length : 0 },
          ]}
        />
      )}
      <MuiTypography variant="subtitle2" sx={{ mt: 3, mb: 1, fontWeight: 600 }}>
        Stats santé · 12 derniers mois
      </MuiTypography>
      {hs.loading ? <MuiTypography variant="body2" color="text.secondary">Chargement…</MuiTypography> : months.length === 0 ? (
        <MuiTypography variant="body2" color="text.secondary">Aucune donnée santé sur la période</MuiTypography>
      ) : (
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ background: "#f8fafc" }}>
              <th style={{ textAlign: "left", padding: "8px", fontWeight: 600 }}>Mois</th>
              <th style={{ textAlign: "right", padding: "8px", fontWeight: 600 }}>Visio</th>
              <th style={{ textAlign: "right", padding: "8px", fontWeight: 600 }}>Consultations</th>
              <th style={{ textAlign: "right", padding: "8px", fontWeight: 600 }}>Pré-consultations</th>
              <th style={{ textAlign: "right", padding: "8px", fontWeight: 600 }}>Questionnaires</th>
              <th style={{ textAlign: "right", padding: "8px", fontWeight: 600 }}>Minutes santé</th>
            </tr>
          </thead>
          <tbody>
            {months.map((m, i) => (
              <tr key={i} style={{ borderTop: "1px solid rgba(15,23,42,0.06)" }}>
                <td style={{ padding: "8px" }}>{m.month}</td>
                <td style={{ padding: "8px", textAlign: "right" }}>{m.visioCount ?? 0}</td>
                <td style={{ padding: "8px", textAlign: "right" }}>{m.consultationsCount ?? 0}</td>
                <td style={{ padding: "8px", textAlign: "right" }}>{m.preConsultationCount ?? 0}</td>
                <td style={{ padding: "8px", textAlign: "right" }}>{m.questionnaireCount ?? 0}</td>
                <td style={{ padding: "8px", textAlign: "right" }}>{m.totalMinutesHealth ?? 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </MuiTypography>
  );
};

const T = TabbedShowLayout.Tab;

export const PatientShow = () => (
  <Show component="div" actions={false}>
    <ProfileHeader kind="patient" actions={<EditButton variant="outlined" label="Modifier" />} />
    <Card>
      <TabbedShowLayout
        syncWithLocation
        tabs={<TabbedShowLayoutTabs variant="scrollable" scrollButtons="auto" />}
        sx={{ "& .RaTabbedShowLayout-content": { p: { xs: 2, md: 3 } } }}
      >
        <T label="Profil">
          <CardGrid min={400}>
            <Section title="Identité & contact">
              <InfoGrid
                cols={2}
                fields={[
                  { label: "Prénom", source: "firstName" },
                  { label: "Nom", source: "lastName" },
                  { label: "Nom marital", source: "lastNameMarried" },
                  { label: "Genre", source: (r) => (r?.gender ? labelOf(GENDER, r.gender) : null) },
                  { label: "Date de naissance", source: "birthdate", type: "date" },
                  { label: "Ville de naissance", source: "birthCity" },
                  { label: "Email", source: "email" },
                  { label: "Téléphone", source: "phoneNumber" },
                ]}
              />
            </Section>
            <Section title="Adresse">
              <InfoGrid
                cols={2}
                fields={[
                  { label: "Libellé", source: "address.adresseName" },
                  { label: "Rue", source: "address.street" },
                  { label: "Code postal", source: "address.postalCode" },
                  { label: "Ville", source: "address.city" },
                  { label: "Pays", source: "address.country" },
                  { label: "Téléphone", source: "address.phone" },
                  { label: "Latitude", source: "address.latitude" },
                  { label: "Longitude", source: "address.longitude" },
                ]}
              />
            </Section>
            <Section title="Compte">
              <InfoGrid
                cols={2}
                fields={[
                  { label: "Inscrit le", source: "creationDate", type: "datetime" },
                  { label: "Dernière mise à jour", source: "updateDate", type: "datetime" },
                  { label: "Email vérifié", source: "emailVerified", type: "bool" },
                  { label: "Vérifié", source: "isVerified", type: "bool" },
                  { label: "Actif", source: "active", type: "bool" },
                  { label: "Activé", source: "enabled", type: "bool" },
                  { label: "Profil complété", source: "profileFilled", type: "bool" },
                  { label: "Premier login", source: "firstLogin", type: "bool" },
                  { label: "Connexion via", source: "providerEnum" },
                  { label: "Type social", source: "socialType" },
                  { label: "Invitation envoyée", source: "invitationSent", type: "bool" },
                  { label: "Invitation envoyée le", source: "invitationSentDate", type: "datetime" },
                ]}
              />
            </Section>
            <Section title="Activité">
              <InfoGrid
                cols={2}
                fields={[
                  { label: "Consultations", source: "statsPatient.nbConsultation" },
                  { label: "Annulations", source: "statsPatient.nbCancellation" },
                  { label: "Paiements", source: "statsPatient.nbPayment" },
                  { label: "Montant dépensé", source: "statsPatient.amountSpend", type: "money" },
                  { label: "Programmes", source: "programCount" },
                  { label: "Séances", source: "sessionCount" },
                  { label: "Minutes totales", source: "totalMinutes" },
                  { label: "Minutes forfait restantes", source: "forfaitRemainingMinutes" },
                ]}
              />
            </Section>
            <Section title="Profil & personnalité">
              <InfoGrid
                cols={1}
                fields={[
                  { label: "Traits", source: "traits", type: "list" },
                  { label: "Centres d'intérêt", source: "hobbies", type: "list" },
                  { label: "Notes admin", source: "notes" },
                ]}
              />
            </Section>
            <Section title="Carte médicale">
              <InfoGrid
                cols={1}
                fields={[
                  { label: "Groupe sanguin", source: "medicalCard.bloodGroup" },
                  { label: "Pathologies", source: "medicalCard.diseases", type: "list" },
                  { label: "Opérations / antécédents", source: "medicalCard.operations", type: "list" },
                  { label: "Médicaments", source: (r) => r?.medicalCard?.medications?.map((m) => m?.name || JSON.stringify(m)), type: "list" },
                ]}
              />
            </Section>
            <Section title="Préférences">
              <InfoGrid
                cols={2}
                fields={[
                  { label: "Notifications SMS", source: "settings.receiveNotificationsBySms", type: "bool" },
                  { label: "Notifications email", source: "settings.receiveNotificationsByEmail", type: "bool" },
                  { label: "Marketing", source: "settings.marketingNotifications", type: "bool" },
                  { label: "Rapport hebdo", source: "settings.weeklyReport", type: "bool" },
                  { label: "Rappel séance (min avant)", source: "settings.consultationReminderMinutesBefore" },
                  { label: "Langue", source: "settings.language" },
                ]}
              />
            </Section>
            <Section title="Codes">
              <InfoGrid
                cols={1}
                fields={[
                  { label: "Code parrainage", source: "referralCode", type: "mono" },
                  { label: "Code du parrain", source: "referrerCode", type: "mono" },
                  { label: "Code affiliation", source: "affiliationCode", type: "mono" },
                ]}
              />
            </Section>
          </CardGrid>
          <TechInfo />
        </T>

        <T label="Rendez-vous" path="rendez-vous">
          <RemoteList buildPath={(r) => `/bookings/${r.id}/PATIENT`} emptyText="Aucun rendez-vous" columns={bookingColumns("pro")} />
        </T>

        <T label="Paiements & factures" path="paiements">
          <Stack spacing={3}>
            <Section title="Forfaits">
              <RemoteList buildPath={(r) => `/forfaits/patient/${r.id}`} emptyText="Aucun forfait" columns={forfaitColumns} />
            </Section>
            <Section title="Factures">
              <RemoteList
                buildPath={(r) => `/documents/patient/${r.id}/factures`}
                emptyText="Aucune facture"
                columns={[
                  { key: "name", label: "Facture", render: (d) => pick(d, "name", "fileName") || "—" },
                  { key: "date", label: "Émise le", render: (d) => fmtDate(pick(d, "createdDate", "createdAt", "bookingDate")) },
                  { key: "booking", label: "Pour le RDV du", render: (d) => pick(d, "bookingTime") || "—" },
                  { key: "dl", label: "", align: "right", render: (d) => <DocumentDownloadButton documentId={pick(d, "id")} label="Télécharger" /> },
                ]}
              />
            </Section>
          </Stack>
        </T>

        <T label="Documents" path="documents">
          <RemoteList
            buildPath={(r) => `/documents/user/${r.id}?userType=PATIENT`}
            emptyText="Aucun document"
            columns={[
              { key: "name", label: "Fichier", render: (d) => pick(d, "name", "fileName") || "—" },
              { key: "type", label: "Type", render: (d) => pick(d, "documentTypeEnum", "documentType", "type") || "—" },
              { key: "date", label: "Ajouté le", render: (d) => fmtDate(pick(d, "createdDate", "uploadedAt", "createdAt")) },
              { key: "dl", label: "", align: "right", render: (d) => <DocumentDownloadButton documentId={pick(d, "id")} /> },
            ]}
          />
        </T>

        <T label="Avis" path="avis">
          <RemoteList
            buildPath={(r) => `/reviews/users/${r.id}/received`}
            emptyText="Aucun avis"
            columns={[
              { key: "rating", label: "Note", render: (a) => <Stars value={pick(a, "rating", "note")} /> },
              { key: "comment", label: "Commentaire", render: (a) => pick(a, "comment", "text") || "—" },
              { key: "author", label: "Auteur", render: (a) => pick(a, "authorName", "author.firstName", "author") || "—" },
              { key: "date", label: "Date", render: (a) => fmtDate(pick(a, "createdAt", "date")) },
            ]}
          />
        </T>

        <T label="Parrainage" path="parrainage">
          <ReferralPanel />
        </T>

        <T label="Proches & relations" path="proches">
          <Stack spacing={3}>
            <Section title="Membres de la famille">
              <RemoteList
                buildPath={(r) => `/users/${r.id}`}
                extract={(json) => (Array.isArray(json?.familyDTOS) ? json.familyDTOS : [])}
                emptyText="Aucun membre déclaré"
                columns={[
                  { key: "name", label: "Nom", render: (m) => fullName(m.firstName, m.lastName, "—") },
                  { key: "birth", label: "Naissance", render: (m) => fmtDate(m.birthdate) },
                  { key: "gender", label: "Genre", render: (m) => labelOf(GENDER, m.gender) },
                  { key: "city", label: "Ville", render: (m) => `${m.postalCode || ""} ${m.city || ""}`.trim() || "—" },
                  { key: "phone", label: "Téléphone", render: (m) => m.phoneNumber || "—" },
                ]}
              />
            </Section>
            <Section title="Proches">
              <RemoteList
                buildPath={(r) => `/users/${r.id}`}
                extract={(json) => (Array.isArray(json?.proches) ? json.proches : [])}
                emptyText="Aucun proche déclaré"
                columns={[
                  { key: "name", label: "Nom", render: (p) => fullName(p.firstName, p.lastName, "—") },
                  { key: "birth", label: "Naissance", render: (p) => fmtDate(p.dateOfBirth) },
                  { key: "gender", label: "Genre", render: (p) => labelOf(GENDER, p.gender) },
                  { key: "email", label: "Email", render: (p) => p.email || "—" },
                  { key: "phone", label: "Téléphone", render: (p) => p.phone || "—" },
                ]}
              />
            </Section>
            <Section title="Connexions (amis)">
              <RemoteList
                buildPath={(r) => `/patients/${r.id}/friends/accepted`}
                emptyText="Aucune connexion acceptée"
                columns={[
                  { key: "with", label: "Ami·e", render: (f) => friendLink(f) },
                  { key: "status", label: "Statut", render: (f) => <StatusChip value={pick(f, "status")} enumObj={FRIENDSHIP_STATUS} /> },
                  { key: "since", label: "Depuis", render: (f) => fmtDate(pick(f, "respondedAt", "createdAt")) },
                ]}
              />
            </Section>
            <Section title="Demandes d'amis reçues">
              <RemoteList
                buildPath={(r) => `/patients/${r.id}/friends/incoming`}
                emptyText="Aucune demande reçue"
                columns={[
                  { key: "from", label: "De", render: (f) => friendLink(f) },
                  { key: "date", label: "Reçue le", render: (f) => fmtDateTime(pick(f, "createdAt")) },
                ]}
              />
            </Section>
            <Section title="Praticiens favoris">
              <RemoteList
                buildPath={(r) => `/patients/${r.id}/favorites`}
                emptyText="Aucun favori"
                columns={[
                  { key: "name", label: "Professionnel", render: (p) => (p?.id ? <RecordLink to={`/professionals/${encodeURIComponent(p.id)}/show`}>{fullName(p.firstName, p.lastName, "Voir la fiche")}</RecordLink> : fullName(p.firstName, p.lastName, "—")) },
                  { key: "speciality", label: "Spécialité", render: (p) => pick(p, "speciality") || "—" },
                  { key: "city", label: "Ville", render: (p) => pick(p, "city", "address.city") || "—" },
                ]}
              />
            </Section>
          </Stack>
        </T>

        <T label="Suivi & santé" path="suivi">
          <Stack spacing={3}>
            <Section title="Programmes de suivi">
              {/* GET /follow-up/programs?patientId={id} */}
              <ReferenceManyField reference="follow-up-programs" target="patientId" label="">
                <FollowUpProgramGrid empty={<MuiTypography sx={{ p: 2 }} variant="body2" color="text.secondary">Aucun programme de suivi</MuiTypography>} />
              </ReferenceManyField>
            </Section>
            <Section title="Exercices assignés">
              <RemoteList
                buildPath={(r) => `/patients/${r.id}/exercises`}
                emptyText="Aucun exercice assigné"
                columns={[
                  { key: "title", label: "Exercice", render: (e) => pick(e, "title", "name") || "—" },
                  { key: "category", label: "Catégorie", render: (e) => pick(e, "category") || "—" },
                  { key: "status", label: "Statut", render: (e) => pick(e, "status") || "—" },
                  { key: "assigned", label: "Assigné le", render: (e) => fmtDate(pick(e, "assignedAt")) },
                  { key: "done", label: "Fait le", render: (e) => fmtDate(pick(e, "completedAt")) },
                ]}
              />
            </Section>
            <Section title="Gamification & statistiques santé">
              <PatientStatsPanel />
            </Section>
          </Stack>
        </T>

        <T label="Messages & notes" path="messages">
          <Stack spacing={3}>
            <Section title="Conversations">
              <RemoteList
                buildPath={(r) => `/chat/all-conversation?recipientId=${r.id}&limit=100`}
                emptyText="Aucune conversation"
                columns={[
                  { key: "with", label: "Interlocuteur", render: (c) => pick(c, "proName", "patientName") || "—" },
                  { key: "content", label: "Dernier message", render: (c) => pick(c, "content") || "—" },
                  { key: "date", label: "Date", render: (c) => fmtDateTime(pick(c, "timestamp", "createdAt")) },
                  { key: "read", label: "Lu", render: (c) => (pick(c, "readByRecipient") ? "Oui" : "Non") },
                ]}
              />
            </Section>
            <Section title="Ajouter une note">
              <NotesPanel />
            </Section>
          </Stack>
        </T>
      </TabbedShowLayout>
    </Card>
  </Show>
);

const friendLink = (f) => {
  const o = f?.otherUser || {};
  const name = fullName(o.firstName, o.lastName);
  return o.id ? <RecordLink to={`/patients/${encodeURIComponent(o.id)}/show`}>{name || "Voir la fiche"}</RecordLink> : name || "—";
};

// Écriture : PUT /users/patient/{id} (resourceMap.patients.updatePath).
// Pas de création (aucun POST /users) : « Inviter un pro / patient ».
const PatientForm = () => (
  <SimpleForm>
    <TextInput source="firstName" label="Prénom" validate={required()} />
    <TextInput source="lastName" label="Nom" validate={required()} />
    <TextInput source="email" label="Email" type="email" validate={[required(), email()]} />
    <TextInput source="phoneNumber" label="Téléphone" />
    <SelectInput source="gender" label="Genre" choices={choicesFromEnum(GENDER)} />
    <SelectInput source="accountStatus" label="Statut du compte" choices={choicesFromEnum(ACCOUNT_STATUS)} />
    <TextInput source="address.street" label="Rue" />
    <TextInput source="address.postalCode" label="Code postal" />
    <TextInput source="address.city" label="Ville" />
  </SimpleForm>
);

export const PatientEdit = () => (
  <Edit mutationMode="pessimistic">
    <PatientForm />
  </Edit>
);

export default {
  list: PatientList,
  show: PatientShow,
  edit: PatientEdit,
};
